import React from 'react';
import { Card, Statistic, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';

export interface ClickableStatCardProps {
  /** Título o etiqueta de la estadística */
  title: string;
  /** Valor a mostrar (puede ser número o string) */
  value: number | string;
  /** Color del borde superior y del valor */
  color?: string;
  /** Icono a mostrar como prefix (opcional) */
  prefix?: React.ReactNode;
  /** Sufijo a mostrar después del valor (ej: "fardos", "kg", etc.) */
  suffix?: string;
  /** Decimales para números (default: 0) */
  precision?: number;
  /** Estado de carga (muestra spinner) */
  loading?: boolean;
  /** Ruta de navegación al hacer click (opcional) */
  detailPath?: string;
  /** Callback al hacer click (opcional, se usa si detailPath no está definido) */
  onClick?: () => void;
  /** Texto adicional debajo del valor */
  subtitle?: string;
  /** Tooltip con información adicional (muestra icono de información) */
  tooltip?: string;
  /** Estilos adicionales para la card */
  style?: React.CSSProperties;
  /** Clase CSS adicional */
  className?: string;
}

/**
 * Componente de tarjeta estadística clickeable usando Statistic de Ant Design
 * Incluye efectos hover y navegación a detalles
 * 
 * @example
 * <ClickableStatCard
 *   title="Aplicación Compras"
 *   value={1234}
 *   color={theme.colors.success}
 *   suffix="fardos"
 *   detailPath="/reportes/compras/detalle/aplicacion-compras"
 * />
 */
export const ClickableStatCard: React.FC<ClickableStatCardProps> = ({
  title,
  value,
  color = theme.colors.primary,
  prefix,
  suffix = '',
  precision = 0,
  loading = false,
  detailPath,
  onClick,
  subtitle,
  tooltip,
  style = {},
  className = ''
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (detailPath) {
      navigate(detailPath);
    } else if (onClick) {
      onClick();
    }
  };

  const isClickable = !!detailPath || !!onClick;

  // Título con icono de información si hay tooltip
  const titleWithTooltip = tooltip ? (
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {title}
      <Tooltip title={tooltip} placement="top">
        <QuestionCircleOutlined 
          style={{ 
            fontSize: '14px', 
            cursor: 'help',
            color: theme.colors.textSecondary
          }} 
          onClick={(e) => {
            e.stopPropagation(); // Evitar que el click en el icono active la navegación
          }}
        />
      </Tooltip>
    </span>
  ) : title;

  return (
    <Card
      className={className}
      hoverable={isClickable}
      onClick={isClickable ? handleClick : undefined}
      style={{
        backgroundColor: '#fff',
        borderRadius: theme.styles.borderRadius,
        boxShadow: theme.styles.boxShadowLight,
        borderTop: `4px solid ${color}`,
        minWidth: '150px',
        flex: '1 1 auto',
        height: '100%',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        ...style
      }}
      styles={{
        body: {
          padding: '16px',
          minHeight: '80px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }
      }}
      onMouseEnter={(e) => {
        if (isClickable) {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (isClickable) {
          e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
          e.currentTarget.style.transform = '';
        }
      }}
    >
      <Statistic
        title={titleWithTooltip}
        value={value}
        prefix={prefix}
        suffix={suffix}
        precision={precision}
        valueStyle={{
          color,
          fontSize: '28px',
          fontWeight: 600
        }}
        loading={loading}
      />
      {subtitle && (
        <div style={{
          fontSize: '11px',
          color: theme.colors.textSecondary,
          marginTop: '8px',
          wordBreak: 'break-word'
        }}>
          {subtitle}
        </div>
      )}
      {isClickable && !loading && (
        <div style={{
          fontSize: '11px',
          color: theme.colors.textSecondary,
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          Click para ver detalles
        </div>
      )}
    </Card>
  );
};

